// pages/Incidencias.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  DatePicker,
  Timeline,
  Descriptions,
  Tabs,
  Alert,
  Avatar,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ToolOutlined,
  WifiOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  FileTextOutlined,
  HomeOutlined,
  CalendarOutlined,
  UserOutlined,
  CommentOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Incidencias = () => {
  // Estados
  const [incidencias, setIncidencias] = useState([]);
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalFinalizarVisible, setModalFinalizarVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentIncidencia, setCurrentIncidencia] = useState(null);
  const [form] = Form.useForm();
  const [finalizarForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    estado: undefined,
    tipo: undefined,
    fechaInicio: undefined,
    fechaFin: undefined
  });
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    pendientes: 0,
    finalizadas: 0,
    mantenimiento: 0,
    limpieza: 0,
    otros: 0
  });

  // Configuración de tipos de incidencia
  const tiposIncidencia = {
    1: { nombre: 'Mantenimiento', color: 'orange', icon: <ToolOutlined /> },
    2: { nombre: 'Limpieza', color: 'green', icon: <WifiOutlined /> },
    3: { nombre: 'Otros', color: 'blue', icon: <WarningOutlined /> }
  };

  // Configuración de estados
  const estadosIncidencia = {
    0: { nombre: 'Pendiente', color: 'warning', status: 'warning', icon: <ClockCircleOutlined /> },
    1: { nombre: 'Finalizada', color: 'success', status: 'success', icon: <CheckCircleOutlined /> }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'ID',
      dataIndex: 'AutoCodigo',
      key: 'AutoCodigo',
      width: 80,
      sorter: (a, b) => a.AutoCodigo - b.AutoCodigo,
      render: (id) => (
        <Tag color="purple" style={{ fontWeight: 'bold' }}>
          #{id}
        </Tag>
      )
    },
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      sorter: (a, b) => a.NumHab - b.NumHab,
      render: (numHab) => (
        <Tag color="cyan" style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <HomeOutlined style={{ marginRight: 4 }} />
          {numHab}
        </Tag>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'Tipo',
      key: 'Tipo',
      width: 120,
      render: (tipo) => {
        const tipoInfo = tiposIncidencia[tipo] || tiposIncidencia[3];
        return (
          <Tag color={tipoInfo.color} icon={tipoInfo.icon}>
            {tipoInfo.nombre}
          </Tag>
        );
      },
      filters: [
        { text: 'Mantenimiento', value: 1 },
        { text: 'Limpieza', value: 2 },
        { text: 'Otros', value: 3 },
      ],
      onFilter: (value, record) => record.Tipo === value,
    },
    {
      title: 'Incidencia',
      dataIndex: 'Incidencia',
      key: 'Incidencia',
      width: 300,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis style={{ maxWidth: 280 }}>
            {text}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Fecha/Hora',
      key: 'fechaHora',
      width: 150,
      sorter: (a, b) => new Date(a.Fecha) - new Date(b.Fecha),
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <CalendarOutlined /> {record.Fecha ? dayjs(record.Fecha).format('DD/MM/YYYY') : '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            <ClockCircleOutlined /> {record.Hora || '-'}
          </div>
        </div>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'Estado',
      key: 'Estado',
      width: 120,
      render: (estado) => {
        const estadoInfo = estadosIncidencia[estado];
        return (
          <Badge 
            status={estadoInfo.status} 
            text={estadoInfo.nombre}
            style={{ fontWeight: 'bold' }}
          />
        );
      },
      filters: [
        { text: 'Pendiente', value: 0 },
        { text: 'Finalizada', value: 1 },
      ],
      onFilter: (value, record) => record.Estado === value,
    },
    {
      title: 'Usuario',
      dataIndex: 'Usuario',
      key: 'Usuario',
      width: 100,
      render: (usuario) => (
        <Tag icon={<UserOutlined />} color="default">
          {usuario}
        </Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          {record.Estado === 0 && (
            <>
              <Tooltip title="Editar">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              
              <Tooltip title="Finalizar">
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  size="small"
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => handleFinalizar(record)}
                />
              </Tooltip>
            </>
          )}
          
          <Popconfirm
            title="¿Eliminar incidencia?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.AutoCodigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchHabitaciones();
    fetchIncidencias();
    fetchEstadisticas();
  }, []);

  const fetchHabitaciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/habitaciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHabitaciones(response.data.data || []);
    } catch (error) {
      console.error('Error cargando habitaciones:', error);
    }
  };

  const fetchIncidencias = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        estado: filters.estado,
        tipo: filters.tipo,
        fechaInicio: filters.fechaInicio,
        fechaFin: filters.fechaFin,
        search: searchText,
        ...params
      };
      
      const response = await axios.get(`${BASE_URL}/api/incidencias`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      setIncidencias(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando incidencias:', error);
      message.error('Error al cargar las incidencias');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/incidencias/estadisticas/resumen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstadisticas(response.data.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const handleCreate = () => {
    setModalType('create');
    setCurrentIncidencia(null);
    form.resetFields();
    form.setFieldsValue({
      Tipo: 1
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentIncidencia(record);
    form.setFieldsValue({
      NumHab: record.NumHab,
      Incidencia: record.Incidencia,
      Tipo: record.Tipo
    });
    setModalVisible(true);
  };

  const handleFinalizar = (record) => {
    setCurrentIncidencia(record);
    finalizarForm.resetFields();
    setModalFinalizarVisible(true);
  };

  const handleView = (record) => {
    const tipoInfo = tiposIncidencia[record.Tipo] || tiposIncidencia[3];
    const estadoInfo = estadosIncidencia[record.Estado];
    
    Modal.info({
      title: `Incidencia #${record.AutoCodigo}`,
      width: 700,
      icon: <WarningOutlined style={{ color: tipoInfo.color }} />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Habitación">
              <Tag color="cyan" style={{ fontSize: '14px' }}>
                <HomeOutlined /> {record.NumHab}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Tipo">
              <Tag color={tipoInfo.color} icon={tipoInfo.icon}>
                {tipoInfo.nombre}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de creación">
              <CalendarOutlined /> {dayjs(record.Fecha).format('DD/MM/YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Hora de creación">
              <ClockCircleOutlined /> {record.Hora}
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              <Badge status={estadoInfo.status} text={estadoInfo.nombre} />
            </Descriptions.Item>
            <Descriptions.Item label="Usuario">
              <UserOutlined /> {record.Usuario}
            </Descriptions.Item>
            <Descriptions.Item label="Descripción" span={2}>
              <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {record.Incidencia}
              </Paragraph>
            </Descriptions.Item>
            
            {record.Estado === 1 && (
              <>
                <Descriptions.Item label="Fecha de finalización">
                  <CalendarOutlined /> {dayjs(record.FechaFinal).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Hora de finalización">
                  <ClockCircleOutlined /> {record.HoraFinal}
                </Descriptions.Item>
                <Descriptions.Item label="Comentario final" span={2}>
                  <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {record.ComentarioFinal}
                  </Paragraph>
                </Descriptions.Item>
                <Descriptions.Item label="Usuario finalización">
                  <UserOutlined /> {record.UsuarioFinal}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
          
          <Divider />
          
          <Alert
            message="Información"
            description="Las incidencias finalizadas pueden ser consultadas en el historial."
            type="info"
            showIcon
          />
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/incidencias/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIncidencias(incidencias.filter(i => i.AutoCodigo !== id));
      message.success('Incidencia eliminada correctamente');
      fetchEstadisticas();
    } catch (error) {
      console.error('❌ Error eliminando incidencia:', error);
      message.error(error.response?.data?.error || 'Error al eliminar la incidencia');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      const usuario = localStorage.getItem('username') || 'Sistema';

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/incidencias`,
          { ...values, Usuario: usuario },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Incidencia creada correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/incidencias/${currentIncidencia.AutoCodigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Incidencia actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchIncidencias();
      fetchEstadisticas();
      
    } catch (error) {
      console.error('❌ Error guardando incidencia:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleFinalizarIncidencia = async () => {
    try {
      const values = await finalizarForm.validateFields();
      const token = localStorage.getItem('token');
      const usuario = localStorage.getItem('username') || 'Sistema';

      await axios.put(
        `${BASE_URL}/api/incidencias/${currentIncidencia.AutoCodigo}/finalizar`,
        { ...values, UsuarioFinal: usuario },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      message.success('Incidencia finalizada correctamente');
      setModalFinalizarVisible(false);
      finalizarForm.resetFields();
      fetchIncidencias();
      fetchEstadisticas();
      
    } catch (error) {
      console.error('❌ Error finalizando incidencia:', error);
      message.error(error.response?.data?.error || 'Error al finalizar la incidencia');
    }
  };

  const handleSearch = () => {
    fetchIncidencias();
  };

  const handleResetFilters = () => {
    setSearchText('');
    setFilters({
      estado: undefined,
      tipo: undefined,
      fechaInicio: undefined,
      fechaFin: undefined
    });
    fetchIncidencias({});
  };

  // Renderizar estadísticas
  const renderEstadisticas = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Total Incidencias"
            value={estadisticas.total}
            prefix={<WarningOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Pendientes"
            value={estadisticas.pendientes}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#faad14' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Finalizadas"
            value={estadisticas.finalizadas}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Mantenimiento"
            value={estadisticas.mantenimiento}
            prefix={<ToolOutlined />}
            valueStyle={{ color: '#fa8c16' }}
          />
        </Card>
      </Col>
    </Row>
  );

  // Barra de búsqueda y filtros
  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space wrap>
        <Input
          placeholder="Buscar por habitación o descripción..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          onPressEnter={handleSearch}
          allowClear
          prefix={<SearchOutlined />}
        />
        
        <Select
          placeholder="Estado"
          style={{ width: 120 }}
          value={filters.estado}
          onChange={(value) => setFilters({ ...filters, estado: value })}
          allowClear
        >
          <Option value={0}>Pendiente</Option>
          <Option value={1}>Finalizada</Option>
        </Select>
        
        <Select
          placeholder="Tipo"
          style={{ width: 130 }}
          value={filters.tipo}
          onChange={(value) => setFilters({ ...filters, tipo: value })}
          allowClear
        >
          <Option value={1}>Mantenimiento</Option>
          <Option value={2}>Limpieza</Option>
          <Option value={3}>Otros</Option>
        </Select>
        
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
        >
          Buscar
        </Button>
        
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetFilters}
        >
          Restablecer
        </Button>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Mantenimiento de Incidencias"
        subtitle={`${estadisticas.pendientes} incidencias pendientes`}
        icon={<ToolOutlined />}
        loading={loading}
        onRefresh={() => {
          fetchIncidencias();
          fetchEstadisticas();
        }}
        onCreate={handleCreate}
        badgeCount={estadisticas.pendientes}
        badgeStatus="warning"
        extra={
          <Space>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => message.info('Función en desarrollo')}
            >
              Reporte
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {renderEstadisticas()}
        {searchBar}
        
        <Table
          columns={columns}
          dataSource={incidencias}
          rowKey="AutoCodigo"
          loading={loading}
          scroll={{ x: 1200 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} incidencias`
          }}
          locale={{
            emptyText: 'No se encontraron incidencias'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar incidencia */}
      <Modal
        title={modalType === 'create' ? 'Nueva Incidencia' : 'Editar Incidencia'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ Tipo: 1 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="NumHab"
                label="Número de Habitación"
                rules={[
                  { required: true, message: 'Seleccione la habitación' }
                ]}
              >
                <Select
                  placeholder="Seleccione habitación"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {habitaciones.map(hab => (
                    <Option key={hab.NumHab} value={hab.NumHab}>
                      <HomeOutlined /> Habitación {hab.NumHab}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Tipo"
                label="Tipo de Incidencia"
                rules={[
                  { required: true, message: 'Seleccione el tipo' }
                ]}
              >
                <Select placeholder="Seleccione tipo">
                  <Option value={1}>
                    <ToolOutlined /> Mantenimiento
                  </Option>
                  <Option value={2}>
                    <WifiOutlined /> Limpieza
                  </Option>
                  <Option value={3}>
                    <WarningOutlined /> Otros
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="Incidencia"
            label="Descripción de la Incidencia"
            rules={[
              { required: true, message: 'Describa la incidencia' },
              { min: 5, message: 'Mínimo 5 caracteres' }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describa detalladamente la incidencia..."
              maxLength={500}
              showCount
            />
          </Form.Item>
          
          <Alert
            message="Información"
            description="La incidencia se registrará con estado 'Pendiente' y podrá ser finalizada posteriormente."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </Form>
      </Modal>

      {/* Modal para finalizar incidencia */}
      <Modal
        title="Finalizar Incidencia"
        open={modalFinalizarVisible}
        onOk={handleFinalizarIncidencia}
        onCancel={() => {
          setModalFinalizarVisible(false);
          finalizarForm.resetFields();
        }}
        width={550}
        okText="Finalizar"
        cancelText="Cancelar"
        okButtonProps={{ style: { backgroundColor: '#52c41a' } }}
      >
        <div style={{ marginBottom: 16 }}>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Habitación">
              <HomeOutlined /> {currentIncidencia?.NumHab}
            </Descriptions.Item>
            <Descriptions.Item label="Tipo">
              {currentIncidencia && tiposIncidencia[currentIncidencia.Tipo]?.nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Descripción">
              <Paragraph ellipsis={{ rows: 2 }}>
                {currentIncidencia?.Incidencia}
              </Paragraph>
            </Descriptions.Item>
          </Descriptions>
        </div>
        
        <Form form={finalizarForm} layout="vertical">
          <Form.Item
            name="ComentarioFinal"
            label="Comentario de Finalización"
            rules={[
              { required: true, message: 'Ingrese un comentario' },
              { min: 3, message: 'Mínimo 3 caracteres' }
            ]}
          >
            <TextArea
              rows={3}
              placeholder="Describa cómo se resolvió la incidencia..."
              maxLength={300}
              showCount
            />
          </Form.Item>
          
          <Alert
            message="Confirmación"
            description="Al finalizar, la incidencia quedará registrada en el historial."
            type="success"
            showIcon
          />
        </Form>
      </Modal>
    </>
  );
};

export default Incidencias;